// Picture uploads (docs/contracts.md section 8): decode the chosen file (EXIF orientation applied), keep its own aspect (no crop: the image goes into the wall's library and may later hang in either
// frame kind; the client crops it to the frame when it loads it), downscale to the largest frame kind's max_px on the
// long side, encode WebP (JPEG where the browser cannot encode WebP, e.g. older Safari), keep it under ART_MAX_BYTES,
// and send it to the server. A small square thumbnail (THUMB_PX, centre crop) is made from the same decode and sent
// after the upload for the library grid. Also: hang a library image, delete one, send a missing thumbnail.
//
// Animated files (2026-10-23): a GIF or animated WebP within the limits (ART_ANIM_MAX_BYTES, ART_ANIM_MAX_PX on the long
// side, ART_ANIM_MAX_FRAMES) is sent as it is. A larger one, or an APNG, is decoded frame by frame, scaled to
// ART_ANIM_MAX_PX and encoded as a GIF (gifenc, loaded on demand; 256 colours per frame), smaller again if still over
// the byte limit. More frames than the limit: refused. The thumbnail is the first frame. An APNG in a browser that
// cannot decode its frames (no ImageDecoder) is uploaded as a still image (its first frame).

import { ART_ANIM_MAX_BYTES, ART_ANIM_MAX_FRAMES, ART_ANIM_MAX_PX, ART_MAX_BYTES, ART_MAX_TITLE_LENGTH, cleanText, type ArtEntry, type ArtFinish, type LibraryItem } from '@lr3d/shared';
import { decodeFrames, sniffAnim } from './anim-decode';
import { isTile } from './tile';
import { editorHeaders } from '../net/editor';

export class ArtError extends Error {}

export interface PreparedImage {
  blob: Blob;
  type: 'image/webp' | 'image/jpeg' | 'image/gif';
  w: number;
  h: number;
  /** frame count of an animation (missing = still) */
  frames?: number;
  /** from the file name, for the hover label */
  title: string | null;
  /** the library thumbnail (null if the browser could not encode one) */
  thumb: Thumb | null;
}

export interface Thumb { blob: Blob; type: 'image/webp' | 'image/jpeg' }

/** Library thumbnails: THUMB_PX square (shown at 64-72 CSS px, so sharp at 2x), centre crop. */
export const THUMB_PX = 144;

/** Larger input files are not decoded at all (a phone photo is ~3-12 MB). */
const MAX_INPUT_BYTES = 60 * 1024 * 1024;
const WEBP_Q = [0.85, 0.75, 0.62];
const JPEG_Q = [0.88, 0.78, 0.65];

export const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';

async function decode(file: Blob): Promise<{ src: CanvasImageSource; w: number; h: number; close(): void }> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return { src: bmp, w: bmp.width, h: bmp.height, close: () => bmp.close() };
    } catch { /* fall back to an <img> (e.g. a browser without the options argument) */ }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return { src: img, w: img.naturalWidth, h: img.naturalHeight, close: () => {} };
  } finally {
    URL.revokeObjectURL(url);
  }
}

const toBlob = (c: HTMLCanvasElement, type: string, q: number) => new Promise<Blob | null>((r) => c.toBlob(r, type, q));

/** A THUMB_PX square thumbnail of the centred square of `src` (w x h px): WebP, else JPEG; null if neither encodes. */
export async function makeThumb(src: CanvasImageSource, w: number, h: number): Promise<Thumb | null> {
  const c = document.createElement('canvas');
  try {
    const side = Math.min(w, h);
    const tile = isTile(w, h);
    const n = tile ? THUMB_PX : Math.max(16, Math.min(THUMB_PX, Math.round(side)));
    c.width = c.height = n;
    const ctx = c.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, n, n);
    if (tile) {
      // a tile: 2 x 2 repeats, pixels kept sharp
      ctx.imageSmoothingEnabled = false;
      for (let i = 0; i < 4; i++) ctx.drawImage(src, (i % 2) * (n / 2), Math.floor(i / 2) * (n / 2), n / 2, n / 2);
    } else {
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(src, (w - side) / 2, (h - side) / 2, side, side, 0, 0, n, n);
    }
    const webp = await toBlob(c, 'image/webp', 0.8);
    if (webp && webp.type === 'image/webp') return { blob: webp, type: 'image/webp' };
    const jpg = await toBlob(c, 'image/jpeg', 0.82);
    return jpg && jpg.type === 'image/jpeg' ? { blob: jpg, type: 'image/jpeg' } : null;
  } catch {
    return null; // e.g. a tainted canvas
  } finally {
    c.width = c.height = 0;
  }
}

/**
 * `maxPx`: the long side limit. `aspect` = a picture width / height to crop to (centred); 0 = keep the whole image
 * (uploads: the library keeps the original aspect).
 */
export async function prepareImage(file: File | Blob, maxPx: number, aspect = 0, progress?: (text: string) => void): Promise<PreparedImage> {
  if (file.size > MAX_INPUT_BYTES) throw new ArtError(`This file is ${(file.size / 1048576).toFixed(0)} MB; the limit is ${MAX_INPUT_BYTES / 1048576} MB.`);
  if (file.type && !file.type.startsWith('image/')) throw new ArtError('This is not an image. Use a JPEG, PNG, WebP or GIF file.');
  if (!aspect) {
    const anim = await prepareAnimated(file, progress);
    if (anim) return anim;
  }
  let img: Awaited<ReturnType<typeof decode>>;
  try {
    img = await decode(file);
  } catch {
    throw new ArtError('This image could not be read. Use a JPEG, PNG, WebP or GIF file.');
  }
  const canvas = document.createElement('canvas');
  try {
    if (!img.w || !img.h) throw new ArtError('This image has no pixels.');
    if (Math.min(img.w, img.h) < 16) throw new ArtError('This image is too small (at least 16 pixels on each side).');
    // the centred part of the image at the frame's picture aspect (cover: the crop fills the frame and only the
    // overhanging sides go), then down to max_px on its long side (never up)
    const a = aspect > 0 ? aspect : img.w / img.h;
    const sw = img.w / img.h > a ? img.h * a : img.w;
    const sh = img.w / img.h > a ? img.h : img.w / a;
    const sx = (img.w - sw) / 2, sy = (img.h - sh) / 2;
    const k = Math.min(1, maxPx / Math.max(sw, sh));
    const w = Math.max(16, Math.round(sw * k)), h = Math.max(16, Math.round(sh * k));
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new ArtError('This browser cannot process images.');
    ctx.fillStyle = '#fff'; // transparent parts on white (the mat), also for JPEG
    ctx.fillRect(0, 0, w, h);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img.src, sx, sy, sw, sh, 0, 0, w, h);
    const title = titleOf(file);
    const thumb = await makeThumb(canvas, w, h);
    if (!aspect && isTile(w, h)) {
      // a tile (small square): no lossy re-encode. WebP, JPEG and one-frame GIF files go as they are; anything else
      // (PNG) becomes a GIF, exact when it has at most 256 colours
      const t = file.type;
      if ((t === 'image/webp' || t === 'image/jpeg' || t === 'image/gif') && file.size <= ART_MAX_BYTES && w === img.w && h === img.h) {
        return { blob: file.slice(0, file.size, t), type: t, w, h, title, thumb };
      }
      return { blob: await tileGif(ctx.getImageData(0, 0, w, h).data, w, h), type: 'image/gif', w, h, title, thumb };
    }
    // WebP if the browser encodes it (toBlob falls back to PNG otherwise), else JPEG
    for (const q of WEBP_Q) {
      const b = await toBlob(canvas, 'image/webp', q);
      if (!b || b.type !== 'image/webp') break;
      if (b.size <= ART_MAX_BYTES) return { blob: b, type: 'image/webp', w, h, title, thumb };
    }
    for (const q of JPEG_Q) {
      const b = await toBlob(canvas, 'image/jpeg', q);
      if (b && b.type === 'image/jpeg' && b.size <= ART_MAX_BYTES) return { blob: b, type: 'image/jpeg', w, h, title, thumb };
    }
    throw new ArtError('This image is still over 2 MB after compression. Try a simpler or smaller image.');
  } finally {
    img.close();
    canvas.width = canvas.height = 0; // Safari keeps canvas memory until the size goes to 0
  }
}

/** The title from the file name, without the characters the server rejects (normalizeTitle) and without half a
 *  surrogate pair at the cut (encodeURIComponent of the X-Art-Title header would throw). */
const titleOf = (file: File | Blob): string | null =>
  (file instanceof File ? cleanText(file.name.replace(/\.[^.]+$/, ''), ART_MAX_TITLE_LENGTH) : '') || null;

const LIMITS = `${ART_ANIM_MAX_BYTES / 1048576} MB, ${ART_ANIM_MAX_PX} px on the long side and ${ART_ANIM_MAX_FRAMES} frames`;

/**
 * An animated file (GIF, animated WebP, APNG) ready to send, or null for a still image (and for an APNG this browser
 * cannot decode frame by frame: it goes the still path).
 */
async function prepareAnimated(file: File | Blob, progress?: (text: string) => void): Promise<PreparedImage | null> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const info = sniffAnim(bytes);
  if (!info || info.frames < 2) return null;
  if (info.frames > ART_ANIM_MAX_FRAMES) throw new ArtError(`This animation has ${info.frames} frames; the limit is ${ART_ANIM_MAX_FRAMES}.`);
  if (Math.min(info.w, info.h) < 16) throw new ArtError('This image is too small (at least 16 pixels on each side).');
  const title = titleOf(file);
  // the thumbnail: the first frame
  let thumb: Thumb | null = null;
  try {
    const first = await createImageBitmap(new Blob([bytes], { type: info.type }));
    try { thumb = await makeThumb(first, first.width, first.height); } finally { first.close(); }
  } catch { /* no thumbnail: the library shows the file */ }
  const type = info.type;
  if (type !== 'image/png' && bytes.length <= ART_ANIM_MAX_BYTES && Math.max(info.w, info.h) <= ART_ANIM_MAX_PX) {
    return { blob: new Blob([bytes], { type }), type, w: info.w, h: info.h, frames: info.frames, title, thumb };
  }
  // too large (bytes or pixels) or APNG: re-encode as a GIF, smaller each try
  const k0 = Math.min(1, ART_ANIM_MAX_PX / Math.max(info.w, info.h));
  let last = '';
  for (const k of [k0, k0 * 0.75, k0 * 0.55]) {
    const w = Math.max(16, Math.round(info.w * k)), h = Math.max(16, Math.round(info.h * k));
    const gif = await encodeGif(bytes, w, h, info.frames, progress);
    if (!gif) return null; // no frame decoder for this file here (APNG without ImageDecoder): the still path
    last = `${w} × ${h}`;
    if (gif.blob.size <= ART_ANIM_MAX_BYTES) return { blob: gif.blob, type: 'image/gif', w, h, frames: gif.frames, title, thumb };
  }
  throw new ArtError(`This animation is over ${ART_ANIM_MAX_BYTES / 1048576} MB even at ${last} px. The limits are ${LIMITS}: try a shorter or smaller animation.`);
}

/** Decode every frame, scale it to w x h (on white) and encode a looping GIF. Null when no decoder handles the file. */
async function encodeGif(bytes: Uint8Array, w: number, h: number, total: number, progress?: (text: string) => void): Promise<{ blob: Blob; frames: number } | null> {
  const { GIFEncoder, quantize, applyPalette } = await import('gifenc');
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const g = c.getContext('2d', { willReadFrequently: true });
  if (!g) throw new ArtError('This browser cannot process images.');
  g.imageSmoothingEnabled = true;
  g.imageSmoothingQuality = 'high';
  const enc = GIFEncoder();
  try {
    const r = await decodeFrames(bytes, (src, i, delay) => {
      g.fillStyle = '#fff';
      g.fillRect(0, 0, w, h);
      g.drawImage(src, 0, 0, w, h);
      const rgba = g.getImageData(0, 0, w, h).data;
      const palette = quantize(rgba, 256);
      enc.writeFrame(applyPalette(rgba, palette), w, h, { palette, delay });
      if (progress && (i % 5 === 0 || i === total - 1)) progress(`Converting the animation to ${w} × ${h} (frame ${i + 1} of ${total})…`);
    }, ART_ANIM_MAX_FRAMES);
    if (!r) return null;
    enc.finish();
    return { blob: new Blob([enc.bytes() as Uint8Array<ArrayBuffer>], { type: 'image/gif' }), frames: r.count };
  } finally {
    c.width = c.height = 0;
  }
}

/** One frame GIF of a tile: its own colours when there are at most 256, else gifenc's quantizer. */
async function tileGif(rgba: Uint8ClampedArray, w: number, h: number): Promise<Blob> {
  const { GIFEncoder, quantize, applyPalette } = await import('gifenc');
  const colours = new Map<number, number>();
  const index = new Uint8Array(w * h);
  let palette: number[][] | null = [];
  for (let i = 0; i < w * h; i++) {
    const key = (rgba[i * 4]! << 16) | (rgba[i * 4 + 1]! << 8) | rgba[i * 4 + 2]!;
    let k = colours.get(key);
    if (k === undefined) {
      if (colours.size === 256) { palette = null; break; }
      k = colours.size;
      colours.set(key, k);
      palette.push([rgba[i * 4]!, rgba[i * 4 + 1]!, rgba[i * 4 + 2]!]);
    }
    index[i] = k;
  }
  const enc = GIFEncoder();
  if (palette) {
    while (palette.length < 2) palette.push([0, 0, 0]);
    enc.writeFrame(index, w, h, { palette });
  } else {
    const p = quantize(rgba, 256);
    enc.writeFrame(applyPalette(rgba, p), w, h, { palette: p });
  }
  enc.finish();
  return new Blob([enc.bytes() as Uint8Array<ArrayBuffer>], { type: 'image/gif' });
}

const MESSAGES: Record<number, string> = {
  401: 'Not connected to the room. Wait for the connection and try again.',
  403: 'Not connected to this room.',
  409: 'Another picture is in the way (the large frame and the panels under it cannot both have a picture).',
  413: `The image is too large (at most 2 MB for a still image, ${ART_ANIM_MAX_BYTES / 1048576} MB for an animation).`,
  415: 'The server accepts only WebP, JPEG and GIF.',
  429: 'Too many changes in a short time. Wait a minute and try again.',
};

interface Answer { ok?: boolean; art?: ArtEntry | null; error?: string; wall?: string; library?: LibraryItem[]; unhung?: string[]; item?: LibraryItem | null }

async function call(url: string, init: RequestInit): Promise<Answer & { art: ArtEntry | null }> {
  let res: Response;
  // the edit session (admin link, net/editor.ts), when this browser has one
  init = { ...init, headers: { ...(init.headers as Record<string, string>), ...editorHeaders() } };
  try {
    res = await fetch(url, init);
  } catch {
    throw new ArtError('The server could not be reached.');
  }
  let body: Answer = {};
  try { body = await res.json(); } catch { /* not JSON */ }
  // a 401 from the edit lock (no or an expired edit session) is not the connection's
  if (res.status === 401 && body.error && /sign in|session/.test(body.error)) {
    onEditRefused();
    throw new ArtError('Only editors can change pictures in this room. Open the admin link again to edit.');
  }
  if (!res.ok || !body.ok) throw new ArtError(MESSAGES[res.status] ?? (body.error ? `The server refused the image: ${body.error}.` : `The server answered ${res.status}.`));
  return { ...body, art: body.art ?? null };
}

/** A write was refused by the edit lock: the app checks the session again (set by main.ts). */
let onEditRefused: () => void = () => {};
export function setEditRefused(f: () => void): void {
  onEditRefused = f;
}

const slotUrl = (base: string, room: string, slot: string) => `${base}/art/${encodeURIComponent(room)}/${encodeURIComponent(slot)}`;
const libUrl = (base: string, room: string, wall: string, hash: string) => `${base}/art-library/${encodeURIComponent(room)}/${encodeURIComponent(wall)}/${hash}`;

/** A frame: the finish (+ `color` for 'custom') and the mat (#rrggbb or "none"); each missing = the slot's default. */
export interface FrameChoice { finish?: ArtFinish; color?: string; mat?: string }

/**
 * `frame`: the frame to hang it in (X-Art-Finish, X-Art-Color, X-Art-Mat). Returns the hung entry and the wall's
 * library as the server has it now.
 */
export async function uploadArt(base: string, room: string, slot: string, token: string, img: PreparedImage, frame: FrameChoice = {}): Promise<{ art: ArtEntry; wall?: string; library?: LibraryItem[] }> {
  const headers: Record<string, string> = { 'content-type': img.type, authorization: `Bearer ${token}` };
  if (img.title) headers['x-art-title'] = encodeURIComponent(img.title);
  if (frame.finish) headers['x-art-finish'] = frame.finish;
  if (frame.finish === 'custom' && frame.color) headers['x-art-color'] = frame.color;
  if (frame.mat) headers['x-art-mat'] = frame.mat;
  const r = await call(slotUrl(base, room, slot), { method: 'POST', body: img.blob, headers });
  if (!r.art) throw new ArtError('The server did not return the picture.');
  return { art: r.art, wall: r.wall, library: r.library };
}

/** Hang an image of the slot's wall library in the slot (nothing is uploaded). */
export async function hangFromLibrary(base: string, room: string, slot: string, token: string, hash: string, frame: FrameChoice = {}): Promise<ArtEntry> {
  const body: Record<string, string> = { hash, ...frameBody(frame) };
  const r = await call(slotUrl(base, room, slot), { method: 'PUT', body: JSON.stringify(body), headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` } });
  if (!r.art) throw new ArtError('The server did not return the picture.');
  return r.art;
}

/** Delete an image from a wall's library; slots showing it are emptied. Returns the new list and the emptied slots. */
export async function deleteFromLibrary(base: string, room: string, wall: string, hash: string, token: string): Promise<{ library: LibraryItem[]; unhung: string[] }> {
  const r = await call(libUrl(base, room, wall, hash), { method: 'DELETE', headers: { authorization: `Bearer ${token}` } });
  return { library: r.library ?? [], unhung: r.unhung ?? [] };
}

/** Send a library image's thumbnail (the server keeps the first one). */
export async function uploadThumb(base: string, room: string, wall: string, hash: string, token: string, thumb: Thumb): Promise<LibraryItem | null> {
  const r = await call(`${libUrl(base, room, wall, hash)}/thumb`, { method: 'PUT', body: thumb.blob, headers: { 'content-type': thumb.type, authorization: `Bearer ${token}` } });
  return r.item ?? null;
}

/** The JSON fields of a frame choice (PUT, PATCH). */
function frameBody(f: FrameChoice): Record<string, string> {
  const out: Record<string, string> = {};
  if (f.finish) out.finish = f.finish;
  if (f.finish === 'custom' && f.color) out.color = f.color;
  if (f.mat) out.mat = f.mat;
  return out;
}

/** Change the frame of the slot's picture (finish, + colour for 'custom', mat); the image stays. */
export async function setArtFrame(base: string, room: string, slot: string, token: string, frame: FrameChoice): Promise<ArtEntry> {
  const r = await call(slotUrl(base, room, slot), {
    method: 'PATCH', body: JSON.stringify(frameBody(frame)), headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
  });
  if (!r.art) throw new ArtError('The server did not return the picture.');
  return r.art;
}

export async function removeArt(base: string, room: string, slot: string, token: string): Promise<void> {
  await call(slotUrl(base, room, slot), { method: 'DELETE', headers: { authorization: `Bearer ${token}` } });
}
